import React from 'react';
import { View, ScrollView, TouchableOpacity, StyleSheet, RefreshControl } from 'react-native';
import { Text } from '@/components';
import { Image } from 'expo-image';
import { Plus, Dog, PawPrint, ChevronRight } from 'lucide-react-native';
import { useAuth } from '@/context/AuthContext';
import { useMyGroups } from '@/lib/queries/groups';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import PackHero from '@/assets/images/pack-hero.svg';
import LottieLoader from '@/components/LottieLoader';

export default function GroupsScreen() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const token = session?.accessToken;
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const groupsQuery = useMyGroups(token);
  const groups = groupsQuery.data ?? [];

  // Family management moved to settings — groups page shows packs only
  const packs = groups.filter((g) => g.type === 'pack');

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={{ paddingTop: insets.top + 16, paddingBottom: insets.bottom + 80 }}
      refreshControl={<RefreshControl refreshing={groupsQuery.isFetching} onRefresh={() => groupsQuery.refetch()} tintColor={theme.accentPrimary} />}
    >
      <View style={styles.header}>
        <Text style={styles.title}>{t('groups.title')}</Text>
        <TouchableOpacity style={styles.createBtn} onPress={() => router.push('/group/create' as any)}>
          <Plus size={20} color={theme.backgroundPrimary} />
        </TouchableOpacity>
      </View>

      {groupsQuery.isLoading ? (
        <View style={styles.center}><LottieLoader size={120} /></View>
      ) : groups.length === 0 ? (
        <View style={styles.center}>
          <PackHero width="100%" height={140} preserveAspectRatio="xMidYMid meet" style={{ marginBottom: 8 }} />
          <Text style={styles.emptyTitle}>{t('groups.emptyTitle')}</Text>
          <Text style={styles.empty}>{t('groups.emptyBody')}</Text>
          {/* Prominent CTA for discoverability + visual balance, matching the Explore empty-state pattern. */}
          <TouchableOpacity style={styles.emptyCta} onPress={() => router.push('/group/create' as any)}>
            <Plus size={18} color={theme.backgroundPrimary} />
            <Text style={styles.emptyCtaText}>{t('groups.createGroup')}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <>
          {packs.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('groups.packs')}</Text>
              {packs.map((g) => <GroupRow key={g.id} group={g} onPress={() => router.push(`/group/${g.id}` as any)} t={t} />)}
            </View>
          )}
        </>
      )}
    </ScrollView>
  );
}

function GroupRow({ group, onPress, t }: { group: any; onPress: () => void; t: any }) {
  const members = group.members ?? [];
  const preview = members.slice(0, 2);
  const overflow = members.length - preview.length;
  return (
    <TouchableOpacity style={styles.row} onPress={onPress} activeOpacity={0.85}>
      <View style={{ flex: 1 }}>
        <Text style={styles.rowName}>{group.name}</Text>
        <Text style={styles.rowSub}>{t('groups.memberCount', { count: members.length })}</Text>
      </View>
      <View style={styles.avatars}>
        <View style={[styles.avatar, styles.avatarPaw]}>
          <PawPrint size={14} color={theme.backgroundPrimary} />
        </View>
        {preview.map((m: any) => (
          m?.image ? (
            <Image key={m.userId} source={m.image} style={styles.avatar} contentFit="cover" cachePolicy="memory-disk" />
          ) : (
            <View key={m.userId} style={[styles.avatar, styles.avatarPlaceholder]}><Dog size={12} color={theme.textMuted} /></View>
          )
        ))}
        {overflow > 0 && (
          <View style={[styles.avatar, styles.avatarOverflow]}>
            <Text style={styles.avatarOverflowText}>{`+${overflow}`}</Text>
          </View>
        )}
      </View>
      <ChevronRight size={20} color={theme.textMuted} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: theme.backgroundPrimary },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, marginBottom: 8 },
  title: { ...Typography.body(theme.textPrimary), fontSize: 26, fontWeight: '700', lineHeight: undefined },
  createBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.accentPrimary, alignItems: 'center' as any, justifyContent: 'center' as any },
  section: { paddingHorizontal: 16, marginTop: 16 },
  sectionTitle: { ...Typography.label(theme.textMuted), marginBottom: 8 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, backgroundColor: theme.backgroundSecondary, borderRadius: 16, marginVertical: 4 },
  rowIcon: { width: 40, height: 40, borderRadius: 20, backgroundColor: theme.backgroundSecondaryVarient, alignItems: 'center', justifyContent: 'center' },
  rowName: { ...Typography.body(theme.textPrimary), fontSize: 16, fontWeight: '600' },
  rowSub: { ...Typography.caption(theme.textMuted), fontSize: 12 },
  avatars: { flexDirection: 'row', marginLeft: 'auto' as any },
  avatar: { width: 28, height: 28, borderRadius: 14, borderWidth: 2, borderColor: theme.backgroundSecondary, marginLeft: -10 },
  avatarPaw: { marginLeft: 0, backgroundColor: theme.accentPrimary, alignItems: 'center' as any, justifyContent: 'center' as any },
  avatarPlaceholder: { alignItems: 'center' as any, justifyContent: 'center' as any, backgroundColor: theme.backgroundSecondaryVarient },
  avatarOverflow: { alignItems: 'center' as any, justifyContent: 'center' as any, backgroundColor: theme.backgroundSecondaryVarient },
  avatarOverflowText: { color: theme.textMuted, fontSize: 10, fontWeight: '700' },
  center: { alignItems: 'center', justifyContent: 'center', paddingVertical: 80, gap: 12 },
  empty: { ...Typography.body(theme.textMuted), textAlign: 'center', paddingHorizontal: 32 },
  emptyTitle: { ...Typography.h2(theme.textPrimary), textAlign: 'center', paddingHorizontal: 32 },
  emptyCta: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: theme.accentPrimary, paddingHorizontal: 22, paddingVertical: 13, borderRadius: 12, marginTop: 8 },
  emptyCtaText: { color: theme.backgroundPrimary, fontWeight: '700', fontSize: 15 },
});